import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";

interface BudgetRangeSliderProps {
  min: number;
  max: number;
  onChange: (min: number, max: number) => void;
  currency?: string;
  className?: string;
  t: (key: string, fallback?: string) => string;
}

export function BudgetRangeSlider({ 
  min, 
  max, 
  onChange, 
  currency = "€",
  className = "",
  t
}: BudgetRangeSliderProps) {
  const [sliderValues, setSliderValues] = useState([min, max]);

  const handleSliderChange = (values: number[]) => {
    setSliderValues(values);
    onChange(values[0], values[1]);
  };

  const handleMinInputChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    if (numValue <= sliderValues[1]) {
      const newValues = [numValue, sliderValues[1]];
      setSliderValues(newValues);
      onChange(newValues[0], newValues[1]);
    }
  };

  const handleMaxInputChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= sliderValues[0]) {
      const newValues = [sliderValues[0], numValue];
      setSliderValues(newValues);
      onChange(newValues[0], newValues[1]);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${currency}${amount.toLocaleString()}`;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <Label className="text-sm font-medium">{t('budget_label', 'Budget Range')}</Label>
      
      {/* Slider */}
      <div className="px-3">
        <Slider
          value={sliderValues}
          onValueChange={handleSliderChange}
          max={10000}
          min={500}
          step={250}
          className="w-full"
        />
      </div>
      
      {/* Range display */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{formatCurrency(sliderValues[0])}</span>
        <span className="text-center">to</span>
        <span>{formatCurrency(sliderValues[1])}</span>
      </div>
      
      {/* Manual input fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="budget-min" className="text-xs">{t('minimum', 'Minimum')}</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              {currency}
            </span>
            <Input
              id="budget-min"
              type="number"
              value={sliderValues[0]}
              onChange={(e) => handleMinInputChange(e.target.value)}
              className="pl-8"
              min={500}
              max={sliderValues[1]}
              step={250}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="budget-max" className="text-xs">{t('maximum', 'Maximum')}</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              {currency}
            </span>
            <Input
              id="budget-max"
              type="number"
              value={sliderValues[1]}
              onChange={(e) => handleMaxInputChange(e.target.value)}
              className="pl-8"
              min={sliderValues[0]}
              max={10000}
              step={250}
            />
          </div>
        </div>
      </div>
    </div>
  );
}